import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, fromJS } from 'immutable'
import _get from 'lodash/get'
import _toNumber from 'lodash/toNumber'
import { get as getConfig } from '../../config/index'
import Link from '../Link/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import { getFormattedContentData, getFeatureMedia } from '../../services/content/index'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_COURSE,
} from '../../services/content-type/index'
import ProgressBar, { PROGRESS_BAR_PINK_GRADIENT } from '../ProgressBar/index'
import TileV2Placeholder from '../Tile.v2/Tile.v2Placeholder'
import { getGraphqlHeroImage } from '../Tile.v2/utils'
import { TILE_V2_DISPLAY_DEFAULT } from '../Tile.v2/constants'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

const THUMB_WIDTH = '400'
const THUMB_HEIGHT = '225'

function MemberHomeFirstTouchTile ({
  rawContent,
  formattedContent,
  loading,
  contentHref,
  onPlayClick,
}) {
  const {
    data, imageUrl, href: hrefFromContent, title,
  } = useMemo(() => {
    const useFormatted = (candidate) => {
      if (!Map.isMap(candidate) || candidate.isEmpty()) {
        return null
      }
      const contentType = candidate.get('contentType') || ''
      const hero = getGraphqlHeroImage({
        data: candidate,
        contentType,
        display: TILE_V2_DISPLAY_DEFAULT,
      }) || candidate.getIn(['images', 0, 'url'], '')
      const path = candidate.get('path') || ''
      const imgTitle = candidate.get('title') || ''
      return {
        data: candidate,
        imageUrl: hero || '',
        href: path,
        title: imgTitle,
      }
    }
    const fromProp = useFormatted(formattedContent)
    if (fromProp) {
      return fromProp
    }
    if (rawContent == null || typeof rawContent !== 'object') {
      return {
        data: Map(), imageUrl: '', href: '', title: '',
      }
    }
    const formatted = getFormattedContentData(fromJS(rawContent))
    if (!Map.isMap(formatted) || formatted.isEmpty()) {
      return {
        data: Map(), imageUrl: '', href: '', title: '',
      }
    }
    return useFormatted(formatted)
  }, [rawContent, formattedContent])

  const href = contentHref || hrefFromContent

  const { displayImageUrl, fallbackImageUrl } = useMemo(() => {
    if (!imageUrl) {
      return { displayImageUrl: '', fallbackImageUrl: '' }
    }
    if (!optimizedImagesEnabled) {
      return { displayImageUrl: imageUrl, fallbackImageUrl: imageUrl }
    }
    try {
      const fallbackImageUrlObject = new URL(imageUrl)
      const imageUrlObject = new URL(imageUrl)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: THUMB_WIDTH,
        h: THUMB_HEIGHT,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: THUMB_WIDTH,
        h: THUMB_HEIGHT,
        fm: 'webp',
        q: '100',
      }
      fallbackImageUrlObject.search = stringifyQuery(fallbackImageOptions)
      imageUrlObject.search = stringifyQuery(imageOptions)
      return {
        displayImageUrl: imageUrlObject.toString(),
        fallbackImageUrl: fallbackImageUrlObject.toString(),
      }
    } catch (e) {
      return { displayImageUrl: imageUrl, fallbackImageUrl: imageUrl }
    }
  }, [imageUrl])

  const watchProgress = useMemo(() => {
    if (!Map.isMap(data) || data.isEmpty()) {
      return { showProgress: false, duration: 0, position: 0 }
    }
    const contentType = data.get('contentType') || ''
    if (
      contentType !== TYPE_CONTENT_SERIES
      && contentType !== TYPE_CONTENT_VIDEO
      && contentType !== TYPE_CONTENT_COURSE
    ) {
      return { showProgress: false, duration: 0, position: 0 }
    }
    const featureMedia = getFeatureMedia({ data, contentType })
    const duration = _toNumber(featureMedia.get('duration', 0)) || 0
    const position = _toNumber(featureMedia.getIn(['userMediaPosition', 'position'], 0)) || 0
    const showProgress = duration > 0 && position > 0
    return {
      showProgress,
      duration,
      position,
    }
  }, [data])

  if (loading) {
    return (
      <div
        className="member-home-first-touch__row-thumb member-home-first-touch__row-thumb--loading"
        aria-busy="true"
        aria-live="polite"
      >
        <TileV2Placeholder display={TILE_V2_DISPLAY_DEFAULT} />
      </div>
    )
  }

  if (!href || !Map.isMap(data) || data.isEmpty()) {
    return <div className="member-home-first-touch__row-thumb member-home-first-touch__row-thumb--empty" aria-hidden />
  }

  const imgSrc = displayImageUrl || imageUrl
  if (!imgSrc) {
    return <div className="member-home-first-touch__row-thumb member-home-first-touch__row-thumb--empty" aria-hidden />
  }

  return (
    <div className="member-home-first-touch__row-thumb">
      <Link
        to={href}
        className="member-home-first-touch__row-thumb-link"
        scrollToTop
        onClick={onPlayClick}
      >
        <Picture
          fallbackSrc={(optimizedImagesEnabled ? fallbackImageUrl : imgSrc) || imgSrc}
          width={THUMB_WIDTH}
          height={THUMB_HEIGHT}
          alt=""
          imageClassName="member-home-first-touch__row-thumb-img"
          loading={IMAGE_LOADING_LAZY}
          sources={optimizedImagesEnabled
            ? [{ type: IMAGE_TYPE_WEBP, srcSet: imgSrc }]
            : []}
        />
      </Link>
      {watchProgress.showProgress ? (
        <div className="tile-v2__progress-bar">
          <ProgressBar
            duration={watchProgress.duration}
            userInfo={Map({ featurePosition: watchProgress.position })}
            type={PROGRESS_BAR_PINK_GRADIENT}
          />
        </div>
      ) : null}
      <span className="member-home-first-touch__row-thumb-sr-only">{title}</span>
    </div>
  )
}

MemberHomeFirstTouchTile.propTypes = {
  rawContent: PropTypes.object,
  formattedContent: ImmutablePropTypes.map,
  loading: PropTypes.bool,
  contentHref: PropTypes.string,
  onPlayClick: PropTypes.func,
}

MemberHomeFirstTouchTile.defaultProps = {
  rawContent: null,
  formattedContent: undefined,
  loading: false,
  contentHref: undefined,
  onPlayClick: undefined,
}

export default MemberHomeFirstTouchTile
